// Privacy Policy for AeroGuard SMS aviation safety platform. Written to
// align with EU GDPR and Kingdom of Saudi Arabia PDPL (Law No. 5/1443H).
import LegalPageLayout from './LegalPageLayout';

const H = ({ children }) => (
  <h2 className="text-white text-xl font-semibold mt-8 mb-2">{children}</h2>
);
const P = ({ children }) => (
  <p className="text-slate-300 text-[15px]">{children}</p>
);

export default function PrivacyPolicyPage({ onBack, companyName = 'AeroGuard SMS' }) {
  return (
    <LegalPageLayout title="Privacy Policy" effective="February 2026" onBack={onBack}>
      <P>
        This Privacy Policy explains how {companyName} ("we", "us", the "Platform")
        processes personal data when operators use the AeroGuard aviation Safety
        Management System. It is written to align with the EU General Data
        Protection Regulation (GDPR) and the Saudi Personal Data Protection
        Law (PDPL, Royal Decree M/19).
      </P>

      <H>Data controller vs processor</H>
      <P>
        Where you are an aviation operator using AeroGuard under a pilot or
        commercial agreement, <strong>you are the data controller</strong> of
        the safety records you upload, and AeroGuard acts as your <strong>data
        processor</strong>. A Data Processing Agreement (DPA) governs that
        relationship and is available on request from your account owner.
      </P>

      <H>Personal data we process</H>
      <ul className="list-disc pl-6 space-y-1 text-slate-300 text-[15px]">
        <li>Account data: name, email, role, department, business-unit membership.</li>
        <li>Authentication data: password hash, token version, last login time, and failed-login counters for brute-force protection.</li>
        <li>Safety-report content: incidents, hazards, findings, corrective actions, meeting notes and any personal data operators choose to include.</li>
        <li>Usage data: pages viewed, actions taken, IP address, and browser fingerprint used for audit logging and abuse prevention.</li>
        <li>Device data: PWA/offline-cache state, and (with your consent) geolocation attached to occurrence reports.</li>
      </ul>

      <H>Purposes and legal bases</H>
      <ul className="list-disc pl-6 space-y-1 text-slate-300 text-[15px]">
        <li>To deliver the Platform under our contract with you (Art. 6(1)(b) GDPR; Art. 6(2) PDPL).</li>
        <li>To meet aviation-safety record-keeping obligations imposed on operators (Art. 6(1)(c)).</li>
        <li>To secure the service and prevent abuse (Art. 6(1)(f) — legitimate interest).</li>
        <li>To send transactional notifications (welcome mail, CAPA reminders, regulator forwards) — same legal basis as delivery of the Platform.</li>
      </ul>

      <H>Data retention</H>
      <P>
        Safety records are retained for a minimum of five (5) years to satisfy
        typical aviation-authority requirements (GACA, EASA, FAA). Account
        records are retained while the account remains active and for 90 days
        after deactivation, unless a longer retention is required by law or by
        your DPA. You may trigger an operational purge from
        Settings → Data Management → Factory Reset.
      </P>

      <H>Third-party sub-processors</H>
      <ul className="list-disc pl-6 space-y-1 text-slate-300 text-[15px]">
        <li>MongoDB Atlas — encrypted database hosting.</li>
        <li>Resend / SendGrid — transactional email delivery.</li>
        <li>OpenAI — natural-language help, dictation and text-polish (payload minimised, no long-term training use per API terms).</li>
      </ul>

      <H>Data-subject rights</H>
      <P>
        Depending on your jurisdiction you may have the right to access,
        correct, delete, restrict, port or object to processing of your
        personal data, and to lodge a complaint with your supervisory
        authority (in the EU) or with the Saudi Data & Artificial Intelligence
        Authority (SDAIA) under PDPL. Requests must be routed through your
        operator's account owner.
      </P>

      <H>Security</H>
      <P>
        The Platform uses TLS in transit, per-tenant scoped queries, JWT
        session tokens with 8-hour expiry, brute-force protection on login,
        and audit logging of privileged actions.
      </P>

      <H>International transfers</H>
      <P>
        Where personal data is transferred outside the country of the
        operator (e.g. to sub-processor infrastructure in the EU or the US),
        we rely on Standard Contractual Clauses and/or PDPL-approved transfer
        mechanisms.
      </P>

      <H>Contact</H>
      <P>
        For privacy questions or DPA requests contact your operator's account
        owner, who can reach the AeroGuard team via the in-app <em>Support</em>
        button. Updated versions of this policy will be published at the same
        URL and communicated by email to the account owner.
      </P>
    </LegalPageLayout>
  );
}
